/**
 * Domain Manager Component
 *
 * Manages custom domains for a project:
 * - Add new domain
 * - View DNS records to configure
 * - Check verification status
 * - Remove domain
 */

import { useState, useMemo, useEffect } from 'react';

// ─── Apex detection (mirrors backend isApexDomain logic) ────
// Apex = bare root domain (example.com). Subdomain = anything with a label
// prefix (www.example.com, shop.example.com).
const MULTI_PART_TLDS = new Set([
  'co.uk',
  'ac.uk',
  'org.uk',
  'gov.uk',
  'me.uk',
  'co.in',
  'net.in',
  'org.in',
  'firm.in',
  'com.au',
  'net.au',
  'org.au',
  'co.nz',
  'net.nz',
  'org.nz',
  'co.za',
  'com.br',
  'net.br',
  'com.mx',
  'com.ar',
  'co.jp',
  'ne.jp',
  'or.jp',
]);
function isApexDomain(hostname: string): boolean {
  const parts = hostname.toLowerCase().split('.');
  if (parts.length < 2) return false;
  const lastTwo = parts.slice(-2).join('.');
  if (MULTI_PART_TLDS.has(lastTwo)) return parts.length === 3;
  return parts.length === 2;
}
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiHelpers } from '@/lib/api';
import { useConfirm } from '@/components/ui/confirm-provider';
import {
  Globe,
  Plus,
  Trash2,
  RefreshCw,
  CheckCircle2,
  Clock,
  AlertCircle,
  Copy,
  Check,
  Loader2,
  X,
  ExternalLink,
  ShieldCheck,
} from 'lucide-react';

interface DomainConfig {
  id: string;
  projectId: string;
  teamId: string;
  domain: string;
  isPrimary: boolean;
  cfStatus: string;
  sslStatus: string;
  sslMethod: string;
  dnsRecords: Array<{
    type: string;
    name: string;
    value: string;
    verified: boolean;
    note?: string;
  }>;
  ownershipVerification?: {
    type: string;
    name: string;
    value: string;
  };
  verifiedAt?: string;
  createdAt: string;
  updatedAt: string;
}

function getStatusBadge(cfStatus: string, sslStatus: string) {
  if (cfStatus === 'active' && sslStatus === 'active') {
    return {
      label: 'Active',
      color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      icon: CheckCircle2,
    };
  }
  if (cfStatus === 'active') {
    return {
      label: 'SSL Pending',
      color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      icon: ShieldCheck,
    };
  }
  if (cfStatus === 'pending_validation') {
    return {
      label: 'Pending DNS',
      color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
      icon: Clock,
    };
  }
  return {
    label: cfStatus,
    color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
    icon: AlertCircle,
  };
}

export default function DomainManager({ projectId }: { projectId: string }) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [copiedValue, setCopiedValue] = useState('');
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const { data, isLoading } = useQuery({
    queryKey: ['domains', projectId],
    queryFn: () => apiHelpers.getDomains(projectId),
  });

  const domains: DomainConfig[] = data?.data?.data || [];

  const verifyMutation = useMutation({
    mutationFn: (domain: string) => apiHelpers.verifyDomain(projectId, domain),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains', projectId] });
      toast.success('Verification triggered');
    },
    onError: () => toast.error('Verification failed'),
  });

  const checkStatusMutation = useMutation({
    mutationFn: (domain: string) => apiHelpers.checkDomainStatus(projectId, domain),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['domains', projectId] });
      const d = res?.data?.data;
      if (d?.cfStatus === 'active' && d?.sslStatus === 'active') {
        toast.success('Domain is active with SSL!');
      } else {
        toast.info(`Status: ${d?.cfStatus || 'unknown'}, SSL: ${d?.sslStatus || 'unknown'}`);
      }
    },
    onError: () => toast.error('Status check failed'),
  });

  const removeMutation = useMutation({
    mutationFn: (domain: string) => apiHelpers.removeDomain(projectId, domain),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains', projectId] });
      toast.success('Domain removed');
    },
    onError: () => toast.error('Failed to remove domain'),
  });

  const handleCopy = async (value: string) => {
    await navigator.clipboard.writeText(value);
    setCopiedValue(value);
    setTimeout(() => setCopiedValue(''), 2000);
  };

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-xl border p-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h3 className="flex items-center gap-2 font-semibold">
              <Globe className="h-5 w-5" />
              Custom Domains
            </h3>
            <p className="text-muted-foreground text-sm">
              Connect your own domain to your published website
            </p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add Domain
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="text-primary h-6 w-6 animate-spin" />
          </div>
        ) : domains.length === 0 ? (
          <div className="py-8 text-center">
            <Globe className="text-muted-foreground mx-auto mb-4 h-12 w-12" />
            <h4 className="mb-2 font-semibold">No custom domains</h4>
            <p className="text-muted-foreground mb-4 text-sm">
              Add a custom domain to serve your website on your own URL
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="text-primary text-sm font-medium hover:underline"
            >
              Add your first domain
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {domains.map((domain) => {
              const status = getStatusBadge(domain.cfStatus, domain.sslStatus);
              const StatusIcon = status.icon;

              return (
                <div key={domain.id} className="rounded-lg border">
                  {/* Domain Header */}
                  <div className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                      <Globe className="text-muted-foreground h-5 w-5" />
                      <div>
                        <p className="font-medium">{domain.domain}</p>
                        <div className="flex items-center gap-2 text-xs">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${status.color}`}
                          >
                            <StatusIcon className="h-3 w-3" />
                            {status.label}
                          </span>
                          {domain.sslStatus === 'active' && (
                            <span className="text-green-600">SSL Active</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => checkStatusMutation.mutate(domain.domain)}
                        disabled={checkStatusMutation.isPending}
                        className="text-muted-foreground hover:bg-muted rounded p-2 transition-colors"
                        title="Check status"
                      >
                        <RefreshCw
                          className={`h-4 w-4 ${checkStatusMutation.isPending ? 'animate-spin' : ''}`}
                        />
                      </button>
                      {domain.cfStatus === 'pending_validation' && (
                        <button
                          onClick={() => verifyMutation.mutate(domain.domain)}
                          disabled={verifyMutation.isPending}
                          className="text-primary hover:bg-primary/10 rounded px-3 py-1.5 text-sm font-medium transition-colors"
                        >
                          {verifyMutation.isPending ? 'Verifying...' : 'Verify Now'}
                        </button>
                      )}
                      <button
                        onClick={async () => {
                          if (
                            await confirm({
                              title: 'Remove domain?',
                              message: `Remove ${domain.domain}? This will disconnect it from your site.`,
                              danger: true,
                            })
                          ) {
                            removeMutation.mutate(domain.domain);
                          }
                        }}
                        disabled={removeMutation.isPending}
                        className="text-muted-foreground hover:bg-muted rounded p-2 transition-colors hover:text-red-600"
                        title="Remove domain"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* DNS Records (show if not yet active) */}
                  {domain.cfStatus !== 'active' &&
                    domain.dnsRecords &&
                    domain.dnsRecords.length > 0 && (
                      <div className="border-t bg-gray-50 p-4 dark:bg-gray-900/50">
                        <p className="mb-3 text-sm font-medium">
                          Add these DNS records at your domain registrar:
                        </p>
                        <div className="space-y-2">
                          {domain.dnsRecords.map((record, i) => (
                            <div
                              key={i}
                              className="flex items-center gap-3 rounded-lg border bg-white p-3 dark:bg-gray-800"
                            >
                              <span className="rounded bg-blue-100 px-2 py-0.5 text-xs font-bold text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                                {record.type}
                              </span>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-muted-foreground text-xs">Name:</span>
                                  <code className="truncate text-xs font-medium">
                                    {record.name}
                                  </code>
                                  <button
                                    onClick={() => handleCopy(record.name)}
                                    className="text-muted-foreground hover:text-foreground shrink-0"
                                  >
                                    {copiedValue === record.name ? (
                                      <Check className="h-3 w-3 text-green-500" />
                                    ) : (
                                      <Copy className="h-3 w-3" />
                                    )}
                                  </button>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-muted-foreground text-xs">Value:</span>
                                  <code className="truncate text-xs font-medium">
                                    {record.value}
                                  </code>
                                  <button
                                    onClick={() => handleCopy(record.value)}
                                    className="text-muted-foreground hover:text-foreground shrink-0"
                                  >
                                    {copiedValue === record.value ? (
                                      <Check className="h-3 w-3 text-green-500" />
                                    ) : (
                                      <Copy className="h-3 w-3" />
                                    )}
                                  </button>
                                </div>
                              </div>
                              {record.verified ? (
                                <CheckCircle2 className="h-4 w-4 shrink-0 text-green-500" />
                              ) : (
                                <Clock className="text-muted-foreground h-4 w-4 shrink-0" />
                              )}
                            </div>
                          ))}
                        </div>
                        {/* Per-record notes (e.g. apex domain instructions) */}
                        {domain.dnsRecords.some((r) => r.note) && (
                          <div className="bg-background mt-3 space-y-2 rounded-lg border p-3 text-xs">
                            {domain.dnsRecords
                              .filter((r) => r.note)
                              .map((r, i) => (
                                <div key={i} className="flex gap-2">
                                  <span className="bg-muted text-muted-foreground inline-flex h-5 shrink-0 items-center rounded px-1.5 font-mono text-[10px] font-bold">
                                    {r.type}
                                  </span>
                                  <p className="text-muted-foreground">{r.note}</p>
                                </div>
                              ))}
                          </div>
                        )}
                        <p className="text-muted-foreground mt-3 text-xs">
                          DNS changes can take up to 24 hours to propagate. Click "Verify Now" after
                          adding the records.
                        </p>
                      </div>
                    )}

                  {/* Active domain info */}
                  {domain.cfStatus === 'active' && domain.sslStatus === 'active' && (
                    <div className="border-t bg-green-50 p-4 dark:bg-green-900/10">
                      <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
                        <CheckCircle2 className="h-4 w-4" />
                        <span>Domain is active and serving your website with SSL</span>
                        <a
                          href={`https://${domain.domain}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-auto inline-flex items-center gap-1 text-green-600 hover:underline dark:text-green-400"
                        >
                          Visit <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Domain Modal */}
      {showAddModal && (
        <AddDomainModal projectId={projectId} onClose={() => setShowAddModal(false)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add Domain Modal
// ---------------------------------------------------------------------------

interface DnsLookupData {
  apex: string;
  nameservers: string[];
  provider: {
    id: string;
    name: string;
    apexCnameSupported: boolean;
    hasFlatteningAlias: boolean;
    dnsEditorName?: string;
    supportsApexRedirect: boolean;
    redirectDocsUrl?: string;
  } | null;
  apexCnameSupported: boolean;
  isRegistered: boolean;
}

function AddDomainModal({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const [domain, setDomain] = useState('');
  const [error, setError] = useState('');
  const [acknowledgedApex, setAcknowledgedApex] = useState(false);
  const [dnsInfo, setDnsInfo] = useState<DnsLookupData | null>(null);
  const [dnsLookupPending, setDnsLookupPending] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Normalize input for apex detection (handles https://, trailing /, uppercase)
  const normalized = useMemo(
    () =>
      domain
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/\/+$/, ''),
    [domain]
  );
  const looksLikeApex =
    normalized.length > 0 && normalized.includes('.') && isApexDomain(normalized);

  // Resolve the customer's DNS provider so we can tailor apex guidance.
  // Debounced (500ms) and only fires for domain-shaped input we haven't
  // already looked up. Tolerant of failure — UI falls back to generic copy.
  useEffect(() => {
    if (!looksLikeApex) {
      setDnsInfo(null);
      setDnsLookupPending(false);
      return;
    }
    if (dnsInfo && dnsInfo.apex === normalized) return; // already have it

    setDnsLookupPending(true);
    const handle = setTimeout(async () => {
      try {
        const res = await apiHelpers.lookupDns(normalized);
        const data = res?.data?.data as DnsLookupData | undefined;
        if (data) setDnsInfo(data);
      } catch {
        /* best-effort */
      } finally {
        setDnsLookupPending(false);
      }
    }, 500);
    return () => {
      clearTimeout(handle);
      setDnsLookupPending(false);
    };
  }, [normalized, looksLikeApex]); // eslint-disable-line react-hooks/exhaustive-deps

  // An apex is allowed-through without acknowledgment only if we positively
  // detected a supporting provider (Cloudflare DNS). Otherwise the user has to
  // either switch to www or explicitly accept the risk.
  const apexAllowedByProvider = looksLikeApex && dnsInfo?.apexCnameSupported === true;
  const apexBlockedByProvider =
    looksLikeApex &&
    dnsInfo !== null &&
    dnsInfo.apexCnameSupported === false &&
    dnsInfo.isRegistered;
  const apexProviderUnknown =
    looksLikeApex &&
    dnsInfo !== null &&
    (!dnsInfo.isRegistered || (dnsInfo.isRegistered && dnsInfo.provider === null));

  const addMutation = useMutation({
    mutationFn: (d: string) => apiHelpers.addDomain(projectId, d),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains', projectId] });
      toast.success('Domain added! Configure the DNS records shown below.');
      onClose();
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error?.message || 'Failed to add domain';
      setError(msg);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!normalized) {
      setError('Please enter a domain');
      return;
    }
    // Allow apex immediately if the DNS provider is known to support it
    // (Cloudflare DNS). Otherwise require explicit acknowledgment.
    if (looksLikeApex && !apexAllowedByProvider && !acknowledgedApex) {
      setError(
        'Apex domains are not supported directly. Please use www or acknowledge the limitation.'
      );
      return;
    }
    addMutation.mutate(normalized);
  };

  const useWwwInstead = () => {
    setDomain(`www.${normalized}`);
    setAcknowledgedApex(false);
    setError('');
  };

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-card w-full max-w-md rounded-xl border shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b p-6">
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <Globe className="h-5 w-5" />
              Add Custom Domain
            </h2>
            <button onClick={onClose} className="hover:bg-muted rounded p-1">
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 p-6">
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/20">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium">Domain *</label>
              <input
                type="text"
                value={domain}
                onChange={(e) => {
                  setDomain(e.target.value);
                  setAcknowledgedApex(false);
                  setError('');
                }}
                placeholder="e.g. www.example.com or shop.mybrand.com"
                className="bg-background focus:ring-primary w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2"
                autoFocus
              />
              <p className="text-muted-foreground text-xs">
                Use a subdomain like <code className="bg-muted rounded px-1">www.example.com</code>{' '}
                or <code className="bg-muted rounded px-1">shop.mybrand.com</code> — not the bare
                root.
              </p>
            </div>

            {/* Apex guidance — tailored to the customer's DNS provider.
                Three states:
                  1. Lookup pending: neutral "checking your DNS" hint
                  2. Provider supports apex (Cloudflare DNS): green "you're good"
                  3. Provider known-unsupported: amber, provider-specific instructions
                  4. Provider unknown / unresolved: amber generic + "verify yourself"
            */}
            {looksLikeApex && dnsLookupPending && !dnsInfo && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-400">
                <p className="flex items-center gap-1.5">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Checking your DNS provider for apex-domain support…
                </p>
              </div>
            )}

            {apexAllowedByProvider && dnsInfo?.provider && (
              <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-xs text-green-900 dark:border-green-800 dark:bg-green-900/20 dark:text-green-200">
                <p className="mb-1 flex items-center gap-1.5 font-semibold">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Apex domain supported on {dnsInfo.provider.name}
                </p>
                <p>
                  Your DNS is on <strong>{dnsInfo.provider.name}</strong>, which resolves
                  CNAME-at-apex in a way Tyashin's SSL validator accepts. You can connect{' '}
                  <code className="bg-muted rounded px-1">{normalized}</code> directly — no www
                  prefix needed.
                </p>
              </div>
            )}

            {apexBlockedByProvider && dnsInfo?.provider && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                <p className="mb-1 flex items-center gap-1.5 font-semibold">
                  <AlertCircle className="h-3.5 w-3.5" />
                  {dnsInfo.provider.name} doesn't support apex domains with Tyashin
                </p>
                <p className="mb-2">
                  We detected your DNS is managed at <strong>{dnsInfo.provider.name}</strong>.
                  {dnsInfo.provider.hasFlatteningAlias ? (
                    <>
                      {' '}
                      They offer an ALIAS/ANAME record for apex, but it flattens to an A record
                      before our SSL validator can see the CNAME chain, so validation fails.
                    </>
                  ) : (
                    <>
                      {' '}
                      They don't support CNAME-at-apex, which is what Tyashin's SSL provider needs.
                    </>
                  )}
                </p>
                <p className="mb-2">
                  <strong>Fix:</strong> connect{' '}
                  <code className="bg-muted rounded px-1">www.{normalized}</code> here,{' '}
                  {dnsInfo.provider.supportsApexRedirect ? (
                    <>
                      then use {dnsInfo.provider.dnsEditorName ?? dnsInfo.provider.name}'s URL
                      forwarding feature to 301-redirect{' '}
                      <code className="bg-muted rounded px-1">{normalized}</code> →{' '}
                      <code className="bg-muted rounded px-1">https://www.{normalized}</code>
                      {dnsInfo.provider.redirectDocsUrl ? (
                        <>
                          {' '}
                          (
                          <a
                            href={dnsInfo.provider.redirectDocsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-0.5 underline hover:no-underline"
                          >
                            {dnsInfo.provider.name} redirect docs
                            <ExternalLink className="h-3 w-3" />
                          </a>
                          ).
                        </>
                      ) : (
                        '.'
                      )}
                    </>
                  ) : (
                    <>
                      then set up a 301 redirect from{' '}
                      <code className="bg-muted rounded px-1">{normalized}</code> at an external
                      redirect service (many registrars include this for free).
                    </>
                  )}
                </p>
                <p className="mb-2 text-[11px] opacity-80">
                  <strong>SEO note:</strong> a permanent (301) redirect is the industry standard —
                  Google consolidates ranking signals onto the www version, same pattern Shopify /
                  Webflow / Stripe use. Avoid 302 ("temporary") which splits signals.
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={useWwwInstead}
                    className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700"
                  >
                    Use www.{normalized} instead
                  </button>
                  {looksLikeApex && (
                    <button
                      type="button"
                      onClick={() =>
                        navigate(
                          `/projects/${projectId}/dns-migration?domain=${encodeURIComponent(
                            normalized
                          )}`
                        )
                      }
                      className="text-xs text-amber-900/80 underline hover:no-underline dark:text-amber-200/80"
                    >
                      Or migrate DNS to Cloudflare →
                    </button>
                  )}
                  <label className="inline-flex items-center gap-1.5 text-xs">
                    <input
                      type="checkbox"
                      checked={acknowledgedApex}
                      onChange={(e) => setAcknowledgedApex(e.target.checked)}
                      className="h-3 w-3"
                    />
                    <span>I understand — try apex anyway</span>
                  </label>
                </div>
              </div>
            )}

            {apexProviderUnknown && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                <p className="mb-1 flex items-center gap-1.5 font-semibold">
                  <AlertCircle className="h-3.5 w-3.5" />
                  Apex domains need CNAME-at-root support
                </p>
                <p className="mb-2">
                  {dnsInfo?.isRegistered
                    ? `We couldn't identify your DNS provider${
                        dnsInfo.nameservers.length > 0
                          ? ` (nameservers: ${dnsInfo.nameservers.slice(0, 2).join(', ')})`
                          : ''
                      }.`
                    : `We couldn't resolve DNS for ${normalized} — make sure it's registered and nameservers are set.`}{' '}
                  Tyashin's SSL provider requires a CNAME record at the apex, which most DNS
                  providers don't support. Check whether yours offers it; if not, use the www
                  version with a 301 redirect.
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={useWwwInstead}
                    className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700"
                  >
                    Use www.{normalized} instead
                  </button>
                  <label className="inline-flex items-center gap-1.5 text-xs">
                    <input
                      type="checkbox"
                      checked={acknowledgedApex}
                      onChange={(e) => setAcknowledgedApex(e.target.checked)}
                      className="h-3 w-3"
                    />
                    <span>My DNS supports it — add anyway</span>
                  </label>
                </div>
              </div>
            )}

            {!looksLikeApex && (
              <div className="rounded-lg bg-blue-50 p-3 text-xs text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
                <p className="mb-1 font-medium">How it works:</p>
                <ol className="list-inside list-decimal space-y-1">
                  <li>Enter your domain name (must be a subdomain like www.example.com)</li>
                  <li>Add the DNS records we provide at your registrar</li>
                  <li>Wait for DNS propagation (usually minutes, up to 24h)</li>
                  <li>SSL certificate is provisioned automatically</li>
                </ol>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="hover:bg-muted flex-1 rounded-lg border px-4 py-2 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  addMutation.isPending ||
                  !domain.trim() ||
                  (looksLikeApex && !apexAllowedByProvider && !acknowledgedApex)
                }
                className="bg-primary text-primary-foreground hover:bg-primary/90 flex flex-1 items-center justify-center rounded-lg px-4 py-2 font-medium disabled:opacity-50"
              >
                {addMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Add Domain'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
